/* The live fleet model behind every v3 page. Reads the same Firestore
   collections, with the same filters, as the classic dashboard (BusTracking):
   organizations, the product's vehicle collection, users (all three org-field
   spellings), fleetAlerts, busRefuelEvents, busAttendance and schoolTrips —
   plus the maintenance engine on the payments backend.

   Errors never blank a page: the last good data stays, `error` says what
   failed and `updatedAt` says when the data is from. A failed load retries
   every 30 seconds ("We will keep trying every 30 seconds"). */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  collection, limit, onSnapshot, orderBy, query, Timestamp, where,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { getFleetCollections } from '../../fleet/fleetPortal';
import { rawProductType } from '../../fleet/fleetConstants';
import { useFleetAlerts } from '../../fleet/useFleetAlerts';
import { severityOf, labelOf } from '../../fleet/alertMeta';
import { materializeBusDoc, openLatLng } from '../../utils/locationCrypto';
import { refuelEventCostKsh, refuelEventLiters, refuelEventTimestampMs } from '../../fleet/fleetRefuelUtils';
import { fetchMaintenance, maintenanceConfigured, MaintRow } from './maintenance';
import { RUN_LITE_FIELDS, runLiteFromData } from '../../utils/routeWindow';
import { selectOrFull } from './firestoreRest';
import type { Bus, BusStatus, FleetAlert, FleetModel, IncidentReport, OrgInfo, PaperDue, Person, Refill, Run, Trip } from './model';

type Raw = Record<string, unknown>;
const DAY = 86400000;
/** What a run row reads, on top of the route summary's fields. Not routePoints:
    a run with neither the ri- nor the map- start and end fields (1 of Hidden
    Leaf's 11 completed runs) no longer gets its route window from its trail.
    The route engine's reconcile writes the ri- fields on completed runs, so
    that is old runs only. */
const RUN_FIELDS = [...RUN_LITE_FIELDS, 'date', 'children', 'pinned', 'pinnedName'];

const toDate = (v: unknown): Date | null => {
  if (!v) return null;
  if (v instanceof Date) return v;
  if (typeof (v as Timestamp).toDate === 'function') return (v as Timestamp).toDate();
  if (typeof v === 'number') return new Date(v);
  if (typeof v === 'string') { const t = Date.parse(v); return Number.isFinite(t) ? new Date(t) : null; }
  return null;
};
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const str = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

/* ── context ── */
const EMPTY_ORG: OrgInfo = { docId: null, code: '', name: '', termLabel: '', termStart: null, termEnd: null, gate: null, productType: 'FULL_FEATURE', raw: {} };
const FleetCtx = createContext<FleetModel | null>(null);
export function useFleet(): FleetModel {
  const f = useContext(FleetCtx);
  if (!f) throw new Error('useFleet outside FleetDataProvider');
  return f;
}

function termLabel(start: Date | null, end: Date | null): string {
  if (!start) return '';
  const now = Date.now();
  if (end && now > end.getTime() + DAY) return 'Term ended';
  if (now < start.getTime()) return `Term starts ${start.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`;
  const week = Math.floor((now - start.getTime()) / (7 * DAY)) + 1;
  return `Term · week ${week}`;
}

/* ── bus status in the design's words ── */
function busStatus(d: Raw, lastSeen: Date | null): { status: BusStatus; label: string; tracking: boolean } {
  const fleetStatus = str(d.status) || 'active';
  if (fleetStatus === 'maintenance' || fleetStatus === 'suspended') return { status: 'off_road', label: 'Off road', tracking: false };
  const route = (d.currentRoute as Raw) || {};
  const rs = str(route.status);
  // Only these two statuses mean "out there right now" (allowlist, as BusTracking).
  const onRoute = rs === 'active' || rs === 'collecting';
  if (onRoute) {
    if (d.trafficDetected === true) return { status: 'late', label: 'Delayed', tracking: true };
    return { status: 'on_route', label: 'On route', tracking: true };
  }
  const finished = toDate(route.finishedAt) || toDate(route.endedAt);
  const today = new Date().toDateString();
  if (rs === 'completed' && finished && finished.toDateString() === today && str(route.type) !== 'evening') {
    return { status: 'at_school', label: 'At school', tracking: false };
  }
  void lastSeen;
  return { status: 'idle', label: 'Idle', tracking: false };
}

interface Built { buses: Bus[]; childrenTotal: number; childIds: { id: string; parentId: string | null }[] }

export const FleetDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { fleetOrgCode, currentUser } = useAuth();
  const code = fleetOrgCode || '';
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt((a) => a + 1), []);

  const [org, setOrg] = useState<OrgInfo>(EMPTY_ORG);
  const [orgReady, setOrgReady] = useState(false);
  const [busDocs, setBusDocs] = useState<{ id: string; data: Raw }[] | null>(null);
  const [userDocs, setUserDocs] = useState<Map<string, Raw>>(new Map());
  const [usersReady, setUsersReady] = useState(false);
  const [refuelDocs, setRefuelDocs] = useState<Map<string, Raw>>(new Map());
  const [runDocs, setRunDocs] = useState<Map<string, Run[]>>(new Map());
  const [tripDocs, setTripDocs] = useState<{ id: string; data: Raw }[]>([]);
  const [maint, setMaint] = useState<MaintRow[] | null>(null);
  const [incidents, setIncidents] = useState<IncidentReport[] | null>(null);
  const [maintError, setMaintError] = useState<string | null>(null);
  const [maintTick, setMaintTick] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const alertsApi = useFleetAlerts(code || null);

  // Auto-retry every 30 s while the last load failed.
  useEffect(() => {
    if (!error) return;
    const t = setTimeout(retry, 30000);
    return () => clearTimeout(t);
  }, [error, retry, attempt]);

  /* organisation */
  useEffect(() => {
    if (!code) { setOrgReady(true); return; }
    const q = query(collection(db, 'organizations'), where('code', '==', code), limit(1));
    let cancelled = false;
    const unsub = onSnapshot(q, (snap) => {
      const docSnap = snap.docs[0];
      const d = (docSnap?.data() || {}) as Raw;
      const termStart = toDate(d.currentTermStartDate);
      const termEnd = toDate(d.currentTermEndDate);
      const base: OrgInfo = {
        docId: docSnap?.id || null, code, name: str(d.name) || code,
        termLabel: termLabel(termStart, termEnd), termStart, termEnd, gate: null,
        productType: rawProductType(d.productType as string | undefined) || 'FULL_FEATURE', raw: d,
      };
      setOrg(base);
      setOrgReady(true);
      if (d.homeLocations) {
        void openLatLng(d.homeLocations).then((p) => { if (!cancelled && p) setOrg((o) => ({ ...o, gate: { lat: p.lat, lng: p.lng } })); });
      }
    }, (e) => { setError(e.message || 'Could not load the school'); setOrgReady(true); });
    return () => { cancelled = true; unsub(); };
  }, [code, attempt]);

  const cols = useMemo(() => getFleetCollections(org.productType), [org.productType]);

  /* buses */
  useEffect(() => {
    if (!code || !orgReady) return;
    const q = query(collection(db, cols.vehicles), where(cols.orgField, '==', code), where('isActive', '==', true));
    let seq = 0;
    const unsub = onSnapshot(q, (snap) => {
      const mine = ++seq;
      void (async () => {
        const list: { id: string; data: Raw }[] = [];
        for (const d of snap.docs) {
          const data = d.data() as Raw;
          if (data.temporaryHire === true) continue; // trip hires are not fleet buses
          list.push({ id: d.id, data: (await materializeBusDoc(data)) as Raw });
        }
        if (mine !== seq) return;
        setBusDocs(list);
        setError(null);
        setUpdatedAt(new Date());
      })();
    }, (e) => { setError(e.message || 'Could not refresh the buses'); setBusDocs((b) => b ?? []); });
    return () => unsub();
  }, [code, orgReady, cols.vehicles, cols.orgField, attempt]);

  /* users — all three org-field spellings, merged by id */
  useEffect(() => {
    if (!code) return;
    const snaps: Record<string, Map<string, Raw>> = {};
    const publish = () => {
      const m = new Map<string, Raw>();
      Object.values(snaps).forEach((s) => s.forEach((v, k) => { if (!m.has(k)) m.set(k, v); }));
      setUserDocs(m);
      setUsersReady(true);
    };
    const unsubs = (['schoolCode', 'organizationCode', 'orgCode'] as const).map((field) =>
      onSnapshot(query(collection(db, 'users'), where(field, '==', code)), (snap) => {
        snaps[field] = new Map(snap.docs.map((d) => [d.id, d.data() as Raw]));
        publish();
      }, () => { snaps[field] = new Map(); publish(); }));
    return () => unsubs.forEach((u) => u());
  }, [code, attempt]);

  const busIdsKey = (busDocs || []).map((b) => b.id).sort().join('|');

  /* refuels — last 120 days, chunks of 30 ids */
  useEffect(() => {
    if (!busIdsKey) { setRefuelDocs(new Map()); return; }
    const ids = busIdsKey.split('|');
    const parts: Record<number, Map<string, Raw>> = {};
    const unsubs: (() => void)[] = [];
    for (let i = 0; i < ids.length; i += 30) {
      const k = i;
      unsubs.push(onSnapshot(query(collection(db, cols.refuelEvents), where('busId', 'in', ids.slice(i, i + 30))), (snap) => {
        parts[k] = new Map(snap.docs.map((d) => [d.id, d.data() as Raw]));
        const m = new Map<string, Raw>();
        Object.values(parts).forEach((p) => p.forEach((v, key) => m.set(key, v)));
        setRefuelDocs(m);
      }, () => { /* rules or index: the fuel page says it has no data */ }));
    }
    return () => unsubs.forEach((u) => u());
  }, [busIdsKey, cols.refuelEvents, attempt]);

  /* runs — newest 100 per bus (the classic dashboard's index), kept to 30 days.
     Read once and refreshed every 10 minutes, not listened to: run documents
     change every few seconds during a trip and a live listener per bus would
     bill for every one of those writes. Only RUN_FIELDS come down (firestoreRest):
     the whole document carries the GPS trail, ~14x the rest. */
  useEffect(() => {
    if (!busIdsKey || !code) { setRunDocs(new Map()); return; }
    const ids = busIdsKey.split('|');
    let cancelled = false;
    const load = async () => {
      const cutoff = Date.now() - 30 * DAY;
      const out = new Map<string, Run[]>();
      await Promise.all(ids.map(async (bid) => {
        try {
          const rows = await selectOrFull({
            collection: 'busAttendance', filters: [['schoolCode', '==', code], ['busId', '==', bid]],
            orderBy: { field: 'routeStartedAt', desc: true }, limit: 100, fields: RUN_FIELDS,
          });
          const runs: Run[] = [];
          rows.forEach((d) => {
            const r = d.data;
            const started = toDate(r.routeStartedAt);
            const finished = toDate(r.routeFinishedAt);
            const date = toDate(r.date) || started || new Date(0);
            if ((finished || date).getTime() < cutoff) return;
            const kids = Array.isArray(r.children) ? (r.children as Raw[]) : [];
            runs.push({
              id: d.id, busId: bid, date, type: str(r.routeType) || 'morning', started, finished,
              km: num(r.distanceKm), status: str(r.status) || null,
              served: kids.filter((c) => ['picked_up', 'dropped_off', 'dropped'].includes(str(c.status))).length,
              total: kids.length,
              lite: runLiteFromData(d.id, r),
              pinnedName: r.pinned === true ? (str(r.pinnedName) || 'Pinned trip') : null,
            });
          });
          out.set(bid, runs);
        } catch { out.set(bid, []); }
      }));
      if (!cancelled) setRunDocs(out);
    };
    void load();
    const t = setInterval(load, 10 * 60 * 1000);
    return () => { cancelled = true; clearInterval(t); };
  }, [busIdsKey, code, attempt]);

  /* school trips */
  useEffect(() => {
    if (!code) return;
    const unsub = onSnapshot(query(collection(db, 'schoolTrips'), where('schoolCode', '==', code), limit(150)),
      (snap) => setTripDocs(snap.docs.map((d) => ({ id: d.id, data: d.data() as Raw }))),
      () => setTripDocs([]));
    return () => unsub();
  }, [code, attempt]);

  /* incident reports (Analytics → Incidents, and the count beside Analytics).
     The rule compares the report's own orgCode, so the query must filter on it.
     Newest first, so a school past the cap keeps its latest reports (the
     orgCode + timestamp index is in firestore.indexes.json); if that index is
     not built on a project yet, the unordered read stands in. */
  useEffect(() => {
    if (!code) return;
    const toReports = (snap: { docs: { id: string; data: () => Raw }[] }) => snap.docs.map((d) => {
      const r = d.data();
      return {
        id: d.id, busId: str(r.busId), issueType: str(r.issueType) || 'Incident', details: str(r.details),
        severity: str(r.severity) || null, at: toDate(r.timestamp) || toDate(r.tripFinishedAt) || toDate(r.createdAt),
        driverId: str(r.driverId) || null, resolved: r.resolved === true, resolvedAt: toDate(r.resolvedAt),
      };
    }).sort((a, b) => (b.at?.getTime() || 0) - (a.at?.getTime() || 0));
    const base = query(collection(db, 'busIssueReports'), where('orgCode', '==', code));
    let unsub = onSnapshot(query(base, orderBy('timestamp', 'desc'), limit(300)),
      (snap) => setIncidents(toReports(snap)),
      () => { unsub = onSnapshot(query(base, limit(300)), (snap) => setIncidents(toReports(snap)), () => setIncidents([])); });
    return () => unsub();
  }, [code, attempt]);

  /* maintenance engine (payments backend) */
  const refreshMaintenance = useCallback(() => setMaintTick((t) => t + 1), []);
  useEffect(() => {
    if (!code || !maintenanceConfigured()) return;
    let cancelled = false;
    fetchMaintenance(code, maintTick > 0)
      .then((rows) => { if (!cancelled) { setMaint(rows); setMaintError(null); } })
      .catch((e: Error) => { if (!cancelled) setMaintError(e.message); });
    return () => { cancelled = true; };
  }, [code, maintTick]);

  /* ── derive the model ── */
  const people = useMemo(() => {
    const drivers: Person[] = [];
    const parents: Person[] = [];
    const busName = (id: string | null) => (id ? (busDocs || []).find((b) => b.id === id)?.data.nickname as string || id : '');
    userDocs.forEach((d, id) => {
      const role = str(d.role);
      const busId = (d.busId as string) || null;
      const kids: Person['children'] = [];
      const add = (c: unknown) => {
        if (typeof c === 'string') { if (c.trim()) kids.push({ id: c, name: c, grade: '', busId: null }); return; }
        if (c && typeof c === 'object') {
          const o = c as Raw;
          const nm = str(o.childName) || str(o.name);
          if (nm && !kids.some((k) => k.name.toLowerCase() === nm.toLowerCase())) kids.push({ id: str(o.childId) || nm, name: nm, grade: str(o.className) || str(o.yearGroup) || str(o.grade), busId: (o.busId as string) || null });
        }
      };
      if (Array.isArray(d.children)) d.children.forEach(add);
      if (Array.isArray(d.linkedChildren)) d.linkedChildren.forEach(add);
      const isDriver = role === 'bus_driver' || role === 'driver' || (!!busId && role !== 'parent' && role !== 'fleet_admin');
      const p: Person = {
        id, name: str(d.displayName) || str(d.name) || (str(d.email).split('@')[0] || 'No name'),
        phone: str(d.phoneNumber) || str(d.phone), email: str(d.email),
        role: role === 'fleet_admin' ? 'admin' : isDriver ? 'driver' : 'parent',
        busId, busName: busName(busId), lastSeen: toDate(d.lastActiveAt) || toDate(d.lastSeen) || toDate(d.lastLoginAt),
        children: kids,
        invite: d.suspended === true ? 'none' : 'active', raw: d,
      };
      if (d.temporaryTripConductor === true || d.temporaryTripParent === true) return;
      if (p.role === 'driver') drivers.push(p);
      else if (p.role === 'parent') parents.push(p);
    });
    drivers.sort((a, b) => a.name.localeCompare(b.name));
    parents.sort((a, b) => a.name.localeCompare(b.name));
    return { drivers, parents };
  }, [userDocs, busDocs]);

  const refills = useMemo<Refill[]>(() => {
    const byBus = new Map((busDocs || []).map((b) => [b.id, b]));
    return Array.from(refuelDocs.entries()).map(([id, d]) => {
      const ms = refuelEventTimestampMs(d);
      const bus = byBus.get(String(d.busId || ''));
      const flag = str(d.efficiencyFlag);
      return {
        id, busId: String(d.busId || ''), busPlate: str(bus?.data.numberPlate) || String(d.busId || ''), busName: str(bus?.data.nickname),
        at: ms ? new Date(ms) : null, litres: refuelEventLiters(d) || null, amount: refuelEventCostKsh(d) || null,
        station: str(d.stationName), odometer: num(d.odometerKm), kmSinceLast: num(d.kmSinceLastRefuel),
        hasReceipt: false,
        verdict: flag === 'needs_review' ? 'high' : flag === 'ok' ? 'ok' : 'unknown',
        raw: d,
      } as Refill;
    }).sort((a, b) => (b.at?.getTime() ?? 0) - (a.at?.getTime() ?? 0));
  }, [refuelDocs, busDocs]);

  const runs = useMemo(() => {
    const all: Run[] = [];
    runDocs.forEach((r) => all.push(...r));
    return all.sort((a, b) => b.date.getTime() - a.date.getTime());
  }, [runDocs]);

  const built = useMemo<Built>(() => {
    const maintBy = new Map((maint || []).map((m) => [m.busId, m]));
    const now = Date.now();
    const childIds: Built['childIds'] = [];
    let childrenTotal = 0;
    const buses = (busDocs || []).map(({ id, data: d }) => {
      const live = d.liveLocation as Raw | undefined;
      const lat = live ? Number(live.lat) : NaN;
      const lng = live ? Number(live.lng) : NaN;
      const lastSeen = toDate(d.liveLocationUpdatedAt) || toDate(live?.timestamp);
      const st = busStatus(d, lastSeen);
      const kids = Array.isArray(d.children) ? (d.children as Raw[]) : [];
      const roster = kids.filter((c) => c.temporaryTripChild !== true && !String(c.childId || '').startsWith('otrip_'));
      childrenTotal += roster.length;
      roster.forEach((c) => childIds.push({ id: str(c.childId) || str(c.childName), parentId: (c.parentId as string) || null }));
      const driverIds = Array.isArray(d.driverIds) ? (d.driverIds as string[]) : [];
      const driverDoc = driverIds.map((x) => userDocs.get(x)).find(Boolean)
        || Array.from(userDocs.entries()).find(([, u]) => u.busId === id && (u.role === 'bus_driver' || u.role === 'driver'))?.[1];
      const km30 = (runDocs.get(id) || []).reduce((s, r) => s + (r.km && r.km > 0 ? r.km : 0), 0);
      const litres30 = refills.filter((r) => r.busId === id && r.at && now - r.at.getTime() < 30 * DAY).reduce((s, r) => s + (r.litres || 0), 0);
      const kmpl = km30 > 0 && litres30 > 0 ? km30 / litres30 : null;
      const normal = num(d.benchmarkKmPerL);
      // Papers and service, from the maintenance engine when it has this bus, else the bus doc.
      const papers: PaperDue[] = [];
      const m = maintBy.get(id);
      const insMs = m?.config.insuranceExpiryMs ?? toDate(d.insuranceExpiry)?.getTime() ?? null;
      const inspMs = m?.config.inspectionExpiryMs ?? toDate(d.inspectionExpiry)?.getTime() ?? null;
      if (insMs) papers.push({ kind: 'insurance', label: 'Insurance', due: new Date(insMs) });
      if (inspMs) papers.push({ kind: 'inspection', label: 'NTSA inspection', due: new Date(inspMs) });
      if (m && m.service && (m.service.remainingKm != null || m.service.overdueKm != null)) {
        const remaining = m.service.overdueKm != null && m.service.overdueKm > 0 ? -m.service.overdueKm : (m.service.remainingKm ?? null);
        papers.push({
          kind: 'service', label: m.service.intervalKm ? `Service, ${Math.round(m.service.intervalKm).toLocaleString('en-GB')} km` : 'Service',
          due: m.service.daysRemaining != null ? new Date(now + m.service.daysRemaining * DAY) : null, dueKm: remaining,
        });
      }
      const expired = papers.find((p) => p.kind !== 'service' && p.due && p.due.getTime() < now);
      const lastRefill = refills.find((r) => r.busId === id);
      let needsLook: string | null = null;
      if (expired) needsLook = `${expired.label} expired`;
      else if (typeof d.locationError === 'string' && d.locationError && st.tracking) needsLook = 'No GPS signal';
      else if (kmpl != null && normal != null && kmpl < normal * 0.9) needsLook = 'Fuel above normal';
      else if (papers.some((p) => p.kind === 'service' && p.dueKm != null && p.dueKm <= 0)) needsLook = 'Service overdue';
      else if (st.status !== 'off_road' && km30 > 0 && (!lastRefill?.at || now - lastRefill.at.getTime() > 7 * DAY)) needsLook = 'No refill in 7 days';
      const bus: Bus = {
        id, name: str(d.nickname) || str(d.numberPlate) || id, plate: str(d.numberPlate) || id,
        // The server and the driver sign-up write vehicleMake / vehicleModel /
        // seatCapacity (driverSignup.js buildBusDoc); make / model / capacity
        // are older spellings (2026-10-28: real buses showed no model or seats).
        model: [str(d.vehicleMake) || str(d.make), str(d.vehicleModel) || str(d.model)].filter(Boolean).join(' '),
        seats: num(d.seatCapacity) ?? num(d.capacity) ?? num(d.seats) ?? num(d.seatCount),
        isSpare: d.isSpare === true || /spare/i.test(str(d.nickname)),
        status: st.status, statusLabel: st.label, lateMin: null,
        aboard: st.tracking ? roster.filter((c) => str(c.status) === 'picked_up').length : null,
        expected: roster.length || null,
        driverId: driverIds[0] || null,
        driverName: driverDoc ? (str(driverDoc.displayName) || str(driverDoc.email).split('@')[0]) : '',
        driverPhone: driverDoc ? (str(driverDoc.phoneNumber) || str(driverDoc.phone)) : '',
        lastSeen, lat: Number.isFinite(lat) ? lat : null, lng: Number.isFinite(lng) ? lng : null,
        heading: live ? num(live.heading) : null, speedKmh: live && num(live.speed) != null ? (num(live.speed) as number) * 3.6 : null,
        tracking: st.tracking, km30: runDocs.has(id) ? km30 : null, kmpl, kmplNormal: normal,
        papers, needsLook, raw: d,
      };
      return bus;
    });
    buses.sort((a, b) => Number(a.isSpare) - Number(b.isSpare) || a.name.localeCompare(b.name));
    return { buses, childrenTotal, childIds };
  }, [busDocs, userDocs, runDocs, refills, maint]);

  const alerts = useMemo<FleetAlert[]>(() => {
    const plate = new Map((busDocs || []).map((b) => [b.id, str(b.data.numberPlate) || b.id]));
    return alertsApi.alerts.map((a) => {
      const sev = severityOf(a.type);
      return {
        id: a.id, severity: sev === 'crit' ? 'urgent' : sev === 'warn' ? 'check' : 'info',
        title: labelOf(a.type) + (a.busName ? ` · ${a.busName}` : ''), body: a.message,
        at: a.timestamp, busId: a.busId || null, busPlate: plate.get(a.busId) || a.busName || '',
        read: a.read, kind: a.type, raw: a as unknown as Raw,
      };
    });
  }, [alertsApi.alerts, busDocs]);

  const trips = useMemo<Trip[]>(() => {
    const now = Date.now();
    return tripDocs.map(({ id, data: d }) => {
      const start = toDate(d.departureAt);
      const end = toDate(d.tripEndsAt);
      const s = str(d.status) || 'planned';
      const started = !!d.departedAt || !!((d.milestones as Raw | undefined)?.departedAt);
      const endEff = end?.getTime() ?? (start ? start.getTime() + 36 * 3600 * 1000 : now + 1);
      const busIds = Array.isArray(d.busIds) ? (d.busIds as string[]) : (d.busId ? [String(d.busId)] : []);
      let status: Trip['status'];
      let statusLabel: string;
      if (s === 'cancelled') { status = 'cancelled'; statusLabel = 'Cancelled'; }
      else if (s === 'completed' || now >= endEff) { status = 'done'; statusLabel = 'Done'; }
      else if (started) { status = 'live'; statusLabel = 'Under way'; }
      else if (!busIds.length) { status = 'needs_driver'; statusLabel = 'Needs a bus'; }
      else { status = 'confirmed'; statusLabel = 'Planned'; }
      return {
        id, title: str(d.title) || 'School trip', destination: str(d.destination), start, end, busIds,
        children: Array.isArray(d.childIds) ? (d.childIds as unknown[]).length : null, status, statusLabel, raw: d,
      };
    }).sort((a, b) => (a.start?.getTime() ?? 0) - (b.start?.getTime() ?? 0));
  }, [tripDocs]);

  const childrenUnlinked = useMemo(() => {
    if (!usersReady) return 0;
    const parentIds = new Set(people.parents.map((p) => p.id));
    return built.childIds.filter((c) => !c.parentId || !parentIds.has(c.parentId)).length;
  }, [built.childIds, people.parents, usersReady]);

  const me = useMemo(() => {
    const u = currentUser;
    const doc = u ? userDocs.get(u.uid) : undefined;
    const email = u?.email || '';
    const stem = email ? email.split('@')[0] : '';
    const own = str(doc?.displayName) || u?.displayName || '';
    /* An account named after its email — every console-made school before
       2026-10-27 — shows the name the school recorded for its admin instead
       (Gabriel: "instead of fpjones4031 … the name of that admin"). */
    const isPrimary = !!email && str(org.raw.adminEmail).toLowerCase() === email.toLowerCase();
    const recorded = isPrimary ? str(org.raw.adminName) : '';
    const name = (own && own !== stem ? own : '') || recorded || own || stem || 'You';
    return { uid: u?.uid || '', name, roleLabel: 'Fleet admin', email };
  }, [currentUser, userDocs, org.raw]);

  const loadedOnce = useRef(false);
  const loading = !loadedOnce.current && (!orgReady || busDocs === null);
  if (!loading) loadedOnce.current = true;

  const value = useMemo<FleetModel>(() => ({
    loading, error, updatedAt, org, me,
    buses: built.buses, drivers: people.drivers, parents: people.parents,
    childrenTotal: built.childrenTotal, childrenUnlinked,
    alerts, refills, trips, runs, incidents, maintenance: maint, maintError, refreshMaintenance, retry,
    markAlertRead: alertsApi.dismissAlert, markAllAlertsRead: alertsApi.dismissAllUnread,
  }), [alertsApi.dismissAlert, alertsApi.dismissAllUnread, loading, error, updatedAt, org, me, built, people, childrenUnlinked, alerts, refills, trips, runs, incidents, maint, maintError, refreshMaintenance, retry]);

  return <FleetCtx.Provider value={value}>{children}</FleetCtx.Provider>;
};

/** For tests and previews: supply a ready-made model instead of the live one. */
export const FleetModelProvider = FleetCtx.Provider;
